import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Form, Select, DatePicker, message, Alert, Spin } from "antd";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import dayjs from "dayjs";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import StudentMultiSelect from "../../../components/common/StudentMultiSelect";

const { Option } = Select;

const BulkAssignmentModal = ({
  visible,
  onCancel,
  onSuccess,
  templates,
  preSelectedTemplate,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [fetchingStudents, setFetchingStudents] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  // Fetch data when modal opens
  useEffect(() => {
    if (visible) {
      fetchAcademicYears();
      fetchTerms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Reset form when modal opens or preSelectedTemplate changes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setStudents([]);
      setSelectedTemplate(null);

      if (preSelectedTemplate) {
        form.setFieldsValue({ requirement_template: preSelectedTemplate });
        handleTemplateChange(preSelectedTemplate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, preSelectedTemplate]);

  const handleTemplateChange = async (templateId) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    setSelectedTemplate(template);

    // Clear previously selected students and pre-fill term and academic year from template
    form.setFieldsValue({
      student_spcs: [],
      term: template.term,
      acadyr: template.acadyr,
    });

    // Fetch students matching template criteria
    await fetchStudents(template);
  };

  const fetchStudents = async (template) => {
    setFetchingStudents(true);
    try {
      const params = [];

      // Always filter for ADMITTED students only
      params.push("status=ADMITTED");

      // Add filters from template
      if (template.class_name)
        params.push(`className=${encodeURIComponent(template.class_name)}`);
      if (template.acadyr)
        params.push(`acadyr=${encodeURIComponent(template.acadyr)}`);

      // Only add day_boarding if it's "Day" or "Boarding", skip if "Both" or empty
      if (template.boarding_type && template.boarding_type !== "Both") {
        params.push(
          `day_boarding=${encodeURIComponent(template.boarding_type)}`,
        );
      } else {
        params.push("day_boarding=");
      }

      // Only add gender if it's "Male" or "Female", skip if "Both" or empty
      if (template.gender && template.gender !== "Both") {
        params.push(`gender=${encodeURIComponent(template.gender)}`);
      } else {
        params.push("gender=");
      }

      const url = `${domainSettings.urls.enrollment.student}${params.length > 0 ? "?" + params.join("&") : ""}`;

      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );

      if (response.ok) {
        setStudents(response.data.results || response.data || []);
      } else {
        Utilities.showError(response, "Failed to fetch students");
        setStudents([]);
      }
    } catch (error) {
      message.error("An error occurred while fetching students");
      console.error(error);
      setStudents([]);
    } finally {
      setFetchingStudents(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (!values.student_spcs || values.student_spcs.length === 0) {
        message.error("Please select at least one student");
        setLoading(false);
        return;
      }

      const payload = {
        student_spcs: values.student_spcs,
        requirement_template: values.requirement_template,
        term: values.term,
        acadyr: values.acadyr,
      };

      // Add optional deadlines if provided
      if (values.payment_deadline) {
        payload.payment_deadline = values.payment_deadline.format("YYYY-MM-DD");
      }
      if (values.pickup_deadline) {
        payload.pickup_deadline = values.pickup_deadline.format("YYYY-MM-DD");
      }

      const response = await Utilities.fetchWithAuth(
        "POST",
        domainSettings.urls.bot.bulkAssignments,
        payload,
        navigate,
      );

      if (response.ok) {
        const created =
          response.data.created_count || values.student_spcs.length;
        message.success(`${created} assignment(s) created successfully`);
        form.resetFields();
        setStudents([]);
        setSelectedTemplate(null);
        onSuccess();
      } else {
        Utilities.showError(response, "Failed to create assignments");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Students Assignment"
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Requirement Template"
          name="requirement_template"
          rules={[{ required: true, message: "Please select a template" }]}
        >
          <Select
            placeholder="Select template"
            showSearch
            optionFilterProp="children"
            onChange={handleTemplateChange}
          >
            {templates
              .filter((template) => template.is_active === 1)
              .map((template) => (
                <Option key={template.id} value={template.id}>
                  {template.name} - {template.class_name} ({template.acadyr})
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Select Students"
          name="student_spcs"
          rules={[
            { required: true, message: "Please select at least one student" },
          ]}
          extra={
            selectedTemplate
              ? `${students.length} student(s) found matching template criteria`
              : "Select a template to see matching students"
          }
        >
          <StudentMultiSelect
            students={students}
            placeholder={
              fetchingStudents
                ? "Loading students..."
                : selectedTemplate
                  ? "Select students to assign"
                  : "Select a template first"
            }
            loading={fetchingStudents}
            disabled={!selectedTemplate || fetchingStudents}
            notFoundContent={
              fetchingStudents ? <Spin size="small" /> : "No students found"
            }
            getStudentDisplay={(student) => {
              const fullName = [
                student.firstName,
                student.middleName,
                student.lastName,
              ]
                .filter(Boolean)
                .join(" ");
              return `${fullName} (${student.spcCode || "No SPC"})`;
            }}
            getStudentValue={(student) => student.spcCode}
          />
        </Form.Item>

        <Form.Item
          label="Term"
          name="term"
          rules={[{ required: true, message: "Please select term" }]}
        >
          <Select placeholder="Select term">
            {Array.isArray(terms) &&
              terms.map((term) => (
                <Option key={term.id} value={term.id}>
                  {term.name}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Academic Year"
          name="acadyr"
          rules={[{ required: true, message: "Please select academic year" }]}
        >
          <Select
            placeholder="Select academic year"
            showSearch
            optionFilterProp="children"
          >
            {Array.isArray(academicYears) &&
              academicYears.map((year) => (
                <Option key={year.id} value={year.acadyr}>
                  {year.acadyr}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item label="Payment Deadline (Optional)" name="payment_deadline">
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            disabledDate={(current) =>
              current && current < dayjs().startOf("day")
            }
          />
        </Form.Item>

        <Form.Item label="Pickup Date" name="pickup_deadline">
          <DatePicker
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            disabledDate={(current) =>
              current && current < dayjs().startOf("day")
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BulkAssignmentModal;
